import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AUTH_CHANGED_EVENT,
  PROFILE_UPDATED_EVENT,
  fetchMe,
  getStoredToken,
  hydrateMeFromCache,
  logout,
} from '../../services/authService';
import { useAuthModal } from '../../context/AuthModalContext';
import { isPublicResumePathname } from '../../utils/routeAccess';
import './Layout.css';

function getInitials(displayName) {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const first = parts[0][0];
  const last = parts[parts.length - 1][0];
  return (first + last).toUpperCase();
}

function IconResume() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function IconPeople() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function IconChart() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function IconInboxReferral() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function IconReferralPanel() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
      <line x1="7" y1="9.5" x2="17" y2="9.5" />
      <line x1="7" y1="13" x2="14" y2="13" />
    </svg>
  );
}

function IconUserCircle() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg className="sidebar-nav__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openLoginModal } = useAuthModal();
  const [me, setMe] = useState(() => hydrateMeFromCache());
  const tokenPresent = !!getStoredToken();
  /** Logged-out visitors on contractor/JD-match pages — limited nav only. */
  const guestBrowsingResume = !tokenPresent && isPublicResumePathname(location.pathname);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      if (!getStoredToken()) {
        setMe(null);
        return;
      }
      const cached = hydrateMeFromCache();
      if (cached) {
        setMe((prev) => (prev ? { ...prev, ...cached } : cached));
      }
      fetchMe()
        .then((data) => {
          if (!cancelled) setMe(data);
        })
        .catch(() => {
          if (!cancelled) setMe(hydrateMeFromCache());
        });
    };

    load();
    window.addEventListener(AUTH_CHANGED_EVENT, load);
    window.addEventListener(PROFILE_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_CHANGED_EVENT, load);
      window.removeEventListener(PROFILE_UPDATED_EVENT, load);
    };
  }, []);

  useEffect(() => {
    const target = location.state?.openAuthModalFor;
    if (typeof target !== 'string' || !target) {
      return;
    }
    openLoginModal(target);
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate, openLoginModal]);

  const meLabel = me
    ? `${me.firstName || ''} ${me.lastName || ''}`.trim() || me.username || me.email || ''
    : '';
  const displayName = guestBrowsingResume ? 'Guest' : meLabel || '…';
  const initials = guestBrowsingResume ? '?' : meLabel ? getInitials(meLabel) : '?';
  const subtitle = guestBrowsingResume ? 'Contractor · JD match (sign in for more)' : me?.email || '';

  const onSignOut = () => {
    logout();
    setMe(null);
    navigate('/resume/contractor', { replace: true });
  };

  const resumeNavItem = {
    to: '/resume',
    label: 'Resume Evaluation',
    icon: IconResume,
    end: false,
  };

  const navItems = guestBrowsingResume
    ? [resumeNavItem]
    : [
        ...(me?.role === 'ADMIN'
          ? [{ to: '/admin/ai-usage', label: 'AI Usage Report', icon: IconChart, end: true }]
          : []),
        resumeNavItem,
        ...(me?.role === 'USER' ? [{ to: '/profile', label: 'Profile', icon: IconUserCircle, end: false }] : []),
        ...(me?.role === 'ADMIN'
          ? [
              { to: '/admin/users', label: 'User management', icon: IconShield, end: true },
              { to: '/admin/referral-requests', label: 'Referral requested', icon: IconInboxReferral, end: true },
              { to: '/admin/referral-panel', label: 'Referral Panel', icon: IconReferralPanel, end: true },
              { to: '/admin/configuration', label: 'Configuration', icon: IconSettings, end: true },
            ]
          : []),
        ...(me?.role !== 'ADMIN'
          ? [{ to: '/employee-refer', label: 'Employee Referral', icon: IconPeople, end: true }]
          : []),
        { to: '/interview-questions', label: 'Interview Questions', icon: IconChat, end: true },
        { to: '/bq-mock', label: 'BQ Mock', icon: IconChat, end: true },
      ];

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Main navigation">
        <div className="sidebar__brand">
          <span className="sidebar__brand-mark">Uplift</span>
          <span className="sidebar__brand-dot">.</span>
          <span className="sidebar__brand-ai">ai</span>
        </div>

        <nav className="sidebar__nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
              }
            >
              <Icon />
              <span className="sidebar__link-text">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__profile" role="group" aria-label="User profile">
            <div className="sidebar__avatar" aria-hidden>
              {initials}
            </div>
            <div className="sidebar__profile-text">
              <span className="sidebar__profile-name">{displayName}</span>
              {subtitle ? <span className="sidebar__profile-sub">{subtitle}</span> : null}
            </div>
          </div>
          {guestBrowsingResume ? (
            <div className="sidebar__guest-actions">
              <button
                type="button"
                className="sidebar__guest-primary"
                onClick={() => openLoginModal(`${location.pathname}${location.search}${location.hash}`)}
              >
                Sign in
              </button>
              <Link to="/register" className="sidebar__guest-secondary">
                Register
              </Link>
            </div>
          ) : (
            <button type="button" className="sidebar__signout" onClick={onSignOut}>
              Sign out
            </button>
          )}
        </div>
      </aside>

      <div className="main-pane">
        <main className="main-pane__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
